import React, { useEffect, useRef, useState } from "react";

import { Box, Container, IconButton } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { motion, useMotionValue, useReducedMotion, useSpring, useTransform } from "framer-motion";
import type { MotionValue, Variants } from "framer-motion";
import {
	ArrowForward,
	CheckCircleOutline,
	Email,
	Facebook,
	Instagram,
	Link as LinkIcon
} from "@mui/icons-material";

import VL_Logo from "../../assets/imgs/vl-logo-transparentBg.svg";
import invitation01Mockup from "../../assets/imgs/template01-mockup.webp";
import invitation02Mockup from "../../assets/imgs/template 02-mockup.webp";
import invitation03Mockup from "../../assets/imgs/template03-mockup.webp";
import dashboardMockup from "../../assets/imgs/dashboard-min.webp";
import galleryMockup from "../../assets/imgs/gallery-min.webp";
import settingsMockup from "../../assets/imgs/settings-min-updated.webp";
import albumMockup from "../../assets/imgs/album-min.webp";
import shareMockup from "../../assets/imgs/share-min.webp";
import notificationIOS from "../../assets/imgs/notification-ios-min.webp";
import notificationAndroid from "../../assets/imgs/notification-android-min.webp";

import {
	LandingContainer,
	HeroSection,
	ShowcaseSection,
	FlowSection,
	FooterSection
} from "./styles";
import { trackEvent } from "../../utils/analytics";
import URLNavigatorPWA from "../../Components/Modals/URLNavigatorPWA";
import Reveal from "../../Components/Reveal";
import PageConversion from "../../Components/PageConversion";
import { PrimaryCta, GhostCta } from "../../Components/PageConversion/styles";
import { getDevicePlatform } from "../../utils/getDevicePlatform";
import { useLazyFetchUserDataQuery } from "../../store/services/auth/auth.service";
import { setAnonId } from "../../utils/getOrCreateAnonId";

type Feature = {
	id: string;
	tab: string;
	title: string;
	sub: string;
	bullets: string[];
	images: { src: string; alt: string }[];
};

const FEATURES: Feature[] = [
	{
		id: "creation",
		tab: "Fast creation",
		title: "Fast Creation",
		sub: "Create, customize, and submit your invitation for review. We'll have it live within 24 hours.",
		bullets: [
			"Browse beautiful invitation designs",
			"Pick a stunning template to start",
			"Customize it your way"
		],
		images: [{ src: invitation01Mockup, alt: "Wedding invitation template preview" }]
	},
	{
		id: "share",
		tab: "Share",
		title: "Share",
		sub: "Share your invitation your way: via direct link, WhatsApp, Messenger, or QR code, so every guest can open it with ease.",
		bullets: [
			"Share your invitation link on social media",
			"Generate a QR code for easy album access",
			"Send the album to guests via email"
		],
		images: [{ src: shareMockup, alt: "Invitation share options" }]
	},
	{
		id: "notifications",
		tab: "Notifications",
		title: "Real-Time Notifications",
		sub: "Whether you're on iOS, Android, or desktop, push notifications keep everyone in the loop at the right moment.",
		bullets: [
			"Get notified the second a guest responds, with no refreshing",
			"Guests get gentle push reminders before the big day",
			"Notifications are tied only to the invitation. No spam, no noise"
		],
		images: [
			{ src: notificationIOS, alt: "Push notification on iOS" },
			{ src: notificationAndroid, alt: "Push notification on Android" }
		]
	},
	{
		id: "rsvp",
		tab: "RSVP",
		title: "RSVP",
		sub: "Get instant responses and manage confirmations with zero stress.",
		bullets: [
			"Guests RSVP with a single click, no sign-up needed",
			"Track accepted, declined, and pending responses in real time",
			"Less stress, more time to celebrate"
		],
		images: [{ src: dashboardMockup, alt: "RSVP dashboard" }]
	},
	{
		id: "gallery",
		tab: "Gallery",
		title: "Gallery",
		sub: "Bring your event to life by uploading photos and videos.",
		bullets: ["Upload photos and videos easily", "Arrange and manage your gallery", "Your moments, beautifully showcased"],
		images: [{ src: galleryMockup, alt: "Gallery upload" }]
	},
	{
		id: "settings",
		tab: "Settings",
		title: "Full Control, Your Way",
		sub: "Smart settings made simple.",
		bullets: ["Built to adapt to your celebration", "Control without the complexity", "One place, full control"],
		images: [{ src: settingsMockup, alt: "Invitation settings" }]
	},
	{
		id: "album",
		tab: "Guest album",
		title: "Guest Album",
		sub: "Every moment, captured and shared by everyone. Guests upload photos and videos so you can relive every memory together.",
		bullets: [
			"No login or app required",
			"iPhone and Android compatible",
			"An album that grows as the celebration happens"
		],
		images: [{ src: albumMockup, alt: "Guest album upload" }]
	}
];

const FLOW = [
	{ verb: "Choose", text: "Browse the template gallery and pick the design that feels like your day." },
	{ verb: "Customize", text: "Add your story, photos, venue and schedule. See every change live." },
	{ verb: "Share", text: "Send one link or QR code, then watch RSVPs and memories arrive." }
];

const FOOTER_COLUMNS = [
	{
		title: "Product",
		links: [
			{ to: "/invitation-templates", label: "Invitations" },
			{ to: "/features", label: "Features" },
			{ to: "/pricing", label: "Pricing" },
			{ to: "/howitworks", label: "How it works" }
		]
	},
	{
		title: "Company",
		links: [
			{ to: "/aboutus", label: "About Us" },
			{ to: "/contactus", label: "Contact Us" },
			{ to: "/login", label: "Login" },
			{ to: "/signup", label: "Sign up" }
		]
	},
	{
		title: "Legal",
		links: [
			{ to: "/refund-policy", label: "Refund Policy" },
			{ to: "/affiliate-policy", label: "Affiliate Policy" },
			{ to: "/privacy-policy", label: "Privacy Policy" },
			{ to: "/terms-and-conditions", label: "Terms & Conditions" }
		]
	}
];

const EASE_OUT: [number, number, number, number] = [0.16, 1, 0.3, 1];

/* Hero entrance: the one orchestrated page-load moment. */
const heroContainer: Variants = {
	hidden: {},
	show: { transition: { staggerChildren: 0.11, delayChildren: 0.1 } }
};
const heroItem: Variants = {
	hidden: { opacity: 0, y: 28 },
	show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: EASE_OUT } }
};

const Phone = ({
	src,
	alt,
	className,
	px,
	py,
	depth,
	enterDelay,
	enterFrom,
	ready,
	onLoaded,
	reduce
}: {
	src: string;
	alt: string;
	className: string;
	px: MotionValue<number>;
	py: MotionValue<number>;
	depth: number;
	enterDelay: number;
	enterFrom: number;
	ready: boolean;
	onLoaded: () => void;
	reduce: boolean | null;
}) => {
	// Pointer-driven parallax lives in motion values, outside React's render cycle.
	const x = useTransform(px, v => v * depth);
	const y = useTransform(py, v => v * depth);
	const hidden = { opacity: 0, y: 60, x: enterFrom };
	return (
		// Entrance and parallax animate x/y on separate layers so they never fight over the same transform.
		<motion.div
			className={"phone-wrap"}
			initial={reduce ? false : hidden}
			animate={ready || reduce ? { opacity: 1, y: 0, x: 0 } : hidden}
			transition={{ duration: 1.1, delay: enterDelay, ease: EASE_OUT }}
		>
			<motion.div className={"phone-wrap"} style={reduce ? undefined : { x, y }}>
				<img
					ref={el => {
						// Cached images can finish loading before React attaches onLoad.
						if (el && el.complete) onLoaded();
					}}
					src={src}
					alt={alt}
					className={`phone ${className}`}
					draggable={false}
					decoding={"async"}
					onLoad={onLoaded}
					onError={onLoaded}
				/>
			</motion.div>
		</motion.div>
	);
};

const LandingV02 = () => {
	const navigate = useNavigate();
	const reduce = useReducedMotion();
	const { isIOS, isIPadOS, isStandalonePWA } = getDevicePlatform();
	const [openNavigateModal, setOpenNavigateModal] = useState(false);
	const [activeFeature, setActiveFeature] = useState(0);
	const [callWhoami, { isLoading }] = useLazyFetchUserDataQuery();
	const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
	const loadedPhones = useRef(new Set<string>());
	const [phonesReady, setPhonesReady] = useState(false);

	// Start the hero entrance only once all three phones are decoded, so they never pop in mid-animation.
	const markPhoneLoaded = (key: string) => () => {
		loadedPhones.current.add(key);
		if (loadedPhones.current.size >= 3) setPhonesReady(true);
	};
	useEffect(() => {
		const fallback = window.setTimeout(() => setPhonesReady(true), 2500);
		return () => window.clearTimeout(fallback);
	}, []);

	const mx = useMotionValue(0);
	const my = useMotionValue(0);
	const px = useSpring(mx, { stiffness: 80, damping: 18 });
	const py = useSpring(my, { stiffness: 80, damping: 18 });

	const handleStageMove = (e: React.PointerEvent<HTMLDivElement>) => {
		if (reduce || e.pointerType === "touch") return;
		const rect = e.currentTarget.getBoundingClientRect();
		mx.set((e.clientX - rect.left) / rect.width - 0.5);
		my.set((e.clientY - rect.top) / rect.height - 0.5);
	};
	const handleStageLeave = () => {
		mx.set(0);
		my.set(0);
	};

	const handleNavigateInvitations = (scope: string) => {
		trackEvent("view_invitations", { scope });
		navigate("/invitation-templates");
	};

	const handleTabKey = (e: React.KeyboardEvent, index: number) => {
		const last = FEATURES.length - 1;
		let next = index;
		if (e.key === "ArrowDown" || e.key === "ArrowRight") next = index === last ? 0 : index + 1;
		else if (e.key === "ArrowUp" || e.key === "ArrowLeft") next = index === 0 ? last : index - 1;
		else return;
		e.preventDefault();
		setActiveFeature(next);
		tabRefs.current[next]?.focus();
	};

	const handleOpenUrlNavigator = async (url: string) => {
		const _url = new URL(url, window.location.origin);
		const pathname = _url.pathname;
		const parts = pathname.split("/").filter(Boolean);

		const invitationId = parts[parts.length - 1]; // last segment
		const shouldSetAnonId = /^[a-fA-F0-9]{24}$/.test(invitationId);
		const anonymousToken = _url.searchParams.get("anonymous_token");
		if (anonymousToken && shouldSetAnonId) {
			setAnonId(invitationId || "", anonymousToken || "");
		}
		const query_token = _url.searchParams.get("token");
		if (query_token) {
			localStorage.setItem("token", query_token!);
			await callWhoami();
		}
		navigate(url);
	};

	return (
		<LandingContainer>
			{(isIOS || isIPadOS) && isStandalonePWA && (
				<Box className={"navigate_url_box"}>
					<IconButton onClick={() => setOpenNavigateModal(true)} aria-label="Open invitation link">
						<LinkIcon />
					</IconButton>
				</Box>
			)}

			<HeroSection>
				<Container>
					<div className={"hero-grid"}>
						<motion.div
							className={"hero-copy"}
							variants={heroContainer}
							initial={reduce ? "show" : "hidden"}
							animate={"show"}
						>
							<motion.h1 variants={heroItem}>Create digital invitations that stand out</motion.h1>
							<motion.p className={"hero-sub"} variants={heroItem}>
								Pick a template, make it yours, and share it with every guest in minutes.
							</motion.p>
							<motion.div className={"hero-actions"} variants={heroItem}>
								<PrimaryCta type={"button"} onClick={() => handleNavigateInvitations("view invitations button")}>
									View Invitations
									<ArrowForward />
								</PrimaryCta>
								<GhostCta as={Link} to={"/howitworks"}>
									How it works
								</GhostCta>
							</motion.div>
						</motion.div>

						<div className={"hero-stage"} onPointerMove={handleStageMove} onPointerLeave={handleStageLeave}>
							<div className={"hero-glow"} />
							<Phone
								src={invitation02Mockup}
								alt={"Invitation template 2 preview"}
								className={"left"}
								ready={phonesReady}
								onLoaded={markPhoneLoaded("left")}
								px={px}
								py={py}
								depth={-26}
								enterDelay={0.55}
								enterFrom={40}
								reduce={reduce}
							/>
							<Phone
								src={invitation03Mockup}
								alt={"Invitation template 3 preview"}
								className={"right"}
								ready={phonesReady}
								onLoaded={markPhoneLoaded("right")}
								px={px}
								py={py}
								depth={-26}
								enterDelay={0.65}
								enterFrom={-40}
								reduce={reduce}
							/>
							<Phone
								src={invitation01Mockup}
								alt={"Invitation template 1 preview"}
								className={"center"}
								ready={phonesReady}
								onLoaded={markPhoneLoaded("center")}
								px={px}
								py={py}
								depth={30}
								enterDelay={0.35}
								enterFrom={0}
								reduce={reduce}
							/>
						</div>
					</div>
				</Container>
			</HeroSection>

			<ShowcaseSection>
				<Container>
					<Reveal>
						<div className={"showcase-head"}>
							<h2>Everything your celebration needs, in one place</h2>
							<p>From the first draft to the last photo, VitesLink keeps your guests and your memories together.</p>
						</div>
					</Reveal>
					<div className={"showcase-grid"}>
						<div>
							<div className={"tab-list"} role={"tablist"} aria-label={"Features"}>
								{FEATURES.map((f, i) => (
									<button
										key={f.id}
										ref={el => {
											tabRefs.current[i] = el;
										}}
										type={"button"}
										role={"tab"}
										id={`tab-${f.id}`}
										aria-selected={activeFeature === i}
										aria-controls={`panel-${f.id}`}
										tabIndex={activeFeature === i ? 0 : -1}
										className={"tab"}
										onClick={() => setActiveFeature(i)}
										onKeyDown={e => handleTabKey(e, i)}
									>
										{activeFeature === i && (
											<motion.span
												layoutId={"tab-pill"}
												className={"tab-pill"}
												transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 380, damping: 32 }}
											/>
										)}
										<span className={"tab-label"}>{f.tab}</span>
									</button>
								))}
							</div>
							<div className={"panels"}>
								{FEATURES.map((f, i) => (
									<div
										key={f.id}
										id={`panel-${f.id}`}
										role={"tabpanel"}
										aria-labelledby={`tab-${f.id}`}
										aria-hidden={activeFeature !== i}
										className={`panel ${activeFeature === i ? "active" : ""}`}
									>
										<h3>{f.title}</h3>
										<p className={"panel-sub"}>{f.sub}</p>
										<ul>
											{f.bullets.map(b => (
												<li key={b}>
													<CheckCircleOutline />
													<span>{b}</span>
												</li>
											))}
										</ul>
									</div>
								))}
							</div>
						</div>
						<div className={"stage"}>
							{FEATURES.map((f, i) => (
								<div
									key={f.id}
									className={`stage-item ${f.images.length > 1 ? "pair" : ""} ${activeFeature === i ? "active" : ""}`}
									aria-hidden={activeFeature !== i}
								>
									{f.images.map(img => (
										<img key={img.src} src={img.src} alt={img.alt} loading={i === 0 ? "eager" : "lazy"} />
									))}
								</div>
							))}
						</div>
					</div>
				</Container>
			</ShowcaseSection>

			<FlowSection>
				<Container>
					<Reveal>
						<div className={"flow-head"}>
							<h2>From idea to invitation in three moves</h2>
						</div>
					</Reveal>
					<div className={"flow-list"}>
						{FLOW.map((item, i) => (
							<Reveal key={item.verb} delay={i * 110} className={"flow-item"}>
								<h3>{item.verb}</h3>
								<p>{item.text}</p>
							</Reveal>
						))}
					</div>
					<Link to={"/howitworks"} className={"flow-link"}>
						See the full walkthrough
						<ArrowForward fontSize={"small"} />
					</Link>
				</Container>
			</FlowSection>

			<PageConversion />

			<FooterSection>
				<Container>
					<div className={"footer-top"}>
						<div className={"brand"}>
							<a href={"/"} className={"brand-link"}>
								<img src={VL_Logo} loading={"lazy"} alt="" width={44} />
								VitesLink
							</a>
							<p>Digital wedding invitations with RSVPs, notifications and a shared guest album.</p>
							<div className={"socials"}>
								<a
									href="https://www.instagram.com/viteslink/"
									className={"social"}
									target={"_blank"}
									rel="noreferrer"
									aria-label="VitesLink on Instagram"
								>
									<Instagram />
								</a>
								<a
									href="https://www.facebook.com/share/1DM6gnk2q7/"
									className={"social"}
									target={"_blank"}
									rel="noreferrer"
									aria-label="VitesLink on Facebook"
								>
									<Facebook />
								</a>
								<a href="mailto:info@viteslink.com" className={"social"} aria-label="Email VitesLink">
									<Email />
								</a>
							</div>
						</div>
						{FOOTER_COLUMNS.map(col => (
							<nav className={"col"} key={col.title} aria-label={col.title}>
								<h4>{col.title}</h4>
								<ul>
									{col.links.map(l => (
										<li key={l.to}>
											<Link to={l.to}>{l.label}</Link>
										</li>
									))}
								</ul>
							</nav>
						))}
					</div>
					<div className={"footer-bottom"}>
						&copy; {new Date().getFullYear()} VitesLink. All rights reserved.
					</div>
				</Container>
			</FooterSection>

			{openNavigateModal && (
				<URLNavigatorPWA
					open={true}
					onClose={() => setOpenNavigateModal(false)}
					handleNavigation={handleOpenUrlNavigator}
					isLoading={isLoading}
				/>
			)}
		</LandingContainer>
	);
};

export default LandingV02;
